import { AnimatePresence } from 'framer-motion';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import ValidationMessage from '@/components/Forms/ValidationMessage';
import { MediaResource, VoiceoverMedia } from '@/types/resources';

import {
  Audio,
  DownloadButton,
  PlayButton,
  Player,
  TimePlayed,
  TotalTime,
  Track,
  calculateTime,
} from './AudioPlayer';
import VideoAudioModal from './VideoAudioModal';

const StyledTrack = styled(Track)`
  cursor: auto;
`;

const StyledValidationMessage = styled(ValidationMessage)`
  border: 1px solid ${({ theme }) => theme.colors.error};
`;

interface Props {
  voiceoverMedia: VoiceoverMedia;
  videoMedia: MediaResource;
  timestamp: string;
  videoVolume: number;
}

const VideoAudioPreview: FC<Props> = ({
  voiceoverMedia,
  videoMedia,
  timestamp,
  videoVolume,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [duration, setDuration] = useState<number>();

  useEffect(() => {
    const { current } = audioRef;

    if (!current) {
      return;
    }

    const loadedmetadataCallback = () => {
      setDuration(current.duration);
    };

    current.addEventListener('loadedmetadata', loadedmetadataCallback);

    return () => {
      current.removeEventListener('loadedmetadata', loadedmetadataCallback);
    };
  }, [voiceoverMedia.id]);

  const isDurationValid = useMemo(() => {
    if (!duration) {
      return true;
    }

    const parts = timestamp.split(' --> ');

    const from = parts[0].split(':');
    const until = parts[1].split(':');

    const maxDuration =
      parseFloat(until[0]) +
      parseFloat(until[1]) -
      (parseFloat(from[0]) + parseFloat(from[1]));

    return duration <= maxDuration;
  }, [duration, timestamp]);

  return (
    <>
      {!isDurationValid && (
        <StyledValidationMessage>
          Voiceover duration is longer than timestamp interval!
        </StyledValidationMessage>
      )}
      <Player>
        <PlayButton type="button" onClick={() => setShowModal(true)}>
          <svg
            width="9"
            height="10"
            viewBox="0 0 9 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.33301 4.9987L0.333008 9.66536L0.333008 0.332031L8.33301 4.9987Z"
              fill="currentColor"
            />
          </svg>
        </PlayButton>
        <StyledTrack $playedPercentage={0}>
          {!!duration && (
            <>
              <TimePlayed $type="extraSmall" as="div">
                00:00
              </TimePlayed>
              <TotalTime $type="extraSmall" as="div">
                {calculateTime(duration)}
              </TotalTime>
            </>
          )}
        </StyledTrack>
        <DownloadButton href={voiceoverMedia.versions.original}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 7.00195L8 10.002L11 7.00195"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 2V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.8569 8.85938V14.0022C14.8569 14.1538 14.7967 14.2991 14.6895 14.4063C14.5823 14.5135 14.437 14.5737 14.2854 14.5737H1.71401C1.56245 14.5737 1.41711 14.5135 1.30995 14.4063C1.20278 14.2991 1.14258 14.1538 1.14258 14.0022V8.85938"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </DownloadButton>
        <Audio ref={audioRef} preload="metadata" key={voiceoverMedia.id}>
          <source src={voiceoverMedia.versions.original} type="audio/mpeg" />
        </Audio>
      </Player>
      <AnimatePresence>
        {showModal && (
          <VideoAudioModal
            videoMedia={videoMedia}
            voiceoverMedia={voiceoverMedia}
            videoVolume={videoVolume}
            timestamp={timestamp}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VideoAudioPreview;
